import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight, Shield, Sparkles, Lock, Search, Shuffle, Eye,
  Zap, Check, X, Menu, X as XIcon, ChevronRight,
  HardDrive, Layers, Image, Video, ArrowUpRight, Star
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
//  DESIGN 2: "LIQUID MONOLITH"
//
//  A bold, typography-forward design with massive serifs,
//  horizontal scroll feature showcases, liquid gradient
//  borders, and a monochrome + single accent approach.
//  Deep blacks, whisper grays, one dramatic violet.
// ─────────────────────────────────────────────────────────────────

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Outfit:wght@100;200;300;400;500;600&display=swap');
  
  @keyframes fadeUp { from{opacity:0;transform:translateY(50px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes slideIn { from{opacity:0;transform:translateX(60px)} to{opacity:1;transform:translateX(0)} }
  @keyframes borderFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes marquee { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
  @keyframes breathe { 0%,100%{opacity:0.03} 50%{opacity:0.08} }
  @keyframes typewriter { from{width:0} to{width:100%} }
  @keyframes cursorBlink { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes countUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }

  .liquid-border {
    position: relative;
  }
  .liquid-border::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, rgba(196,181,253,0.3), transparent, rgba(196,181,253,0.1), transparent);
    background-size: 300% 300%;
    animation: borderFlow 6s ease infinite;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
  .mono-glass {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.04);
  }
  .mono-glass-hover:hover {
    background: rgba(255,255,255,0.04);
    border-color: rgba(255,255,255,0.08);
  }
  .text-gradient {
    background: linear-gradient(135deg, #C4B5FD 0%, #E9D5FF 50%, #C4B5FD 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .scroll-container {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .scroll-container::-webkit-scrollbar { display: none; }
`;

// ─── InView hook ───
const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

// ─── Animated counter ───
const Counter: React.FC<{ value: string; label: string; delay: number }> = ({ value, label, delay }) => {
  const { ref, inView } = useInView();
  return (
    <div ref={ref} className="text-center" style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms` }}>
      <div className="text-5xl md:text-6xl font-light text-gradient mb-2" style={{ fontFamily: SERIF }}>{value}</div>
      <div className="text-white/15 text-xs tracking-[.2em] uppercase" style={{ fontFamily: SANS, fontWeight: 200 }}>{label}</div>
    </div>
  );
};

// ─── Feature showcase (horizontal cards) ───
const FeatureShowcase: React.FC<{
  icon: React.ReactNode; title: string; desc: string; num: string; accent: string;
}> = ({ icon, title, desc, num, accent }) => (
  <div className="flex-shrink-0 w-[340px] md:w-[400px] liquid-border rounded-3xl p-8 mono-glass group cursor-default"
    style={{ transition: 'all 0.4s ease' }}>
    <div className="flex items-center justify-between mb-8">
      <span className="text-[10px] tracking-[.3em] uppercase text-white/10" style={{ fontFamily: SANS, fontWeight: 300 }}>{num}</span>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}10`, border: `1px solid ${accent}15` }}>
        {icon}
      </div>
    </div>
    <h3 className="text-2xl font-light text-white/80 mb-4" style={{ fontFamily: SERIF }}>{title}</h3>
    <p className="text-white/20 text-sm leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>{desc}</p>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

const HomepageDesign2: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [heroReady, setHeroReady] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setTimeout(() => setHeroReady(true), 200); }, []);

  const handleGetStarted = () => { window.location.href = '/'; };
  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{ background: '#060510' }}>
      <style>{STYLES}</style>

      {/* Subtle grid background */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.01) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.01) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        <div className="absolute inset-0" style={{ animation: 'breathe 8s ease-in-out infinite', background: 'radial-gradient(ellipse at 20% 50%, rgba(196,181,253,0.06) 0%, transparent 60%)' }} />
      </div>

      {/* ─── Nav ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(6,5,16,0.8)', backdropFilter: 'blur(40px) saturate(1.8)', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/refract-logo.png" alt="Refract" className="w-9 h-9 rounded-lg" />
            <span className="text-base tracking-[.15em] uppercase text-white/50" style={{ fontFamily: SANS, fontWeight: 300 }}>Refract</span>
          </div>
          <div className="hidden md:flex items-center gap-10">
            {['Features', 'Process', 'Pricing'].map(t => (
              <button key={t} onClick={() => scrollTo(t.toLowerCase())}
                className="text-white/20 hover:text-white/50 transition-colors text-[13px] tracking-wide" style={{ fontFamily: SANS, fontWeight: 300 }}>{t}</button>
            ))}
            <button onClick={handleGetStarted}
              className="px-7 py-2.5 rounded-full text-[13px] tracking-wide transition-all duration-300 hover:scale-[1.03]"
              style={{ background: 'rgba(196,181,253,0.1)', border: '1px solid rgba(196,181,253,0.2)', color: '#C4B5FD', fontFamily: SANS, fontWeight: 400 }}>
              Get Started <ArrowUpRight size={13} className="inline ml-1" />
            </button>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white/30">
            {menuOpen ? <XIcon size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden px-8 py-6 space-y-5 border-t border-white/5" style={{ background: 'rgba(6,5,16,0.97)' }}>
            {['Features', 'Process', 'Pricing'].map(t => (
              <button key={t} onClick={() => scrollTo(t.toLowerCase())} className="block text-white/30 text-sm w-full text-left" style={{ fontFamily: SANS }}>{t}</button>
            ))}
            <button onClick={handleGetStarted} className="w-full py-3 rounded-full text-sm" style={{ background: 'rgba(196,181,253,0.15)', color: '#C4B5FD', fontFamily: SANS }}>Get Started</button>
          </div>
        )}
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-8 pt-24 pb-16">
        {/* Decorative line */}
        <div className="absolute top-28 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-transparent via-white/10 to-transparent"
          style={{ opacity: heroReady ? 1 : 0, transition: 'opacity 1.5s ease 0.5s' }} />

        <div className="relative z-10 max-w-5xl mx-auto text-center" style={{ marginTop: '3rem' }}>
          {/* Eyebrow */}
          <div style={{ opacity: heroReady ? 1 : 0, transform: heroReady ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s cubic-bezier(0.16,1,0.3,1) 0.2s' }}>
            <span className="text-[10px] tracking-[.4em] uppercase text-white/20" style={{ fontFamily: SANS, fontWeight: 300 }}>
              Private Media Library
            </span>
          </div>

          {/* Massive headline */}
          <h1 className="mt-8 mb-8" style={{ opacity: heroReady ? 1 : 0, transform: heroReady ? 'translateY(0)' : 'translateY(40px)', transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.35s' }}>
            <span className="block text-7xl md:text-[9rem] font-light leading-[0.85] tracking-tight text-white/90" style={{ fontFamily: SERIF }}>
              Your
            </span>
            <span className="block text-7xl md:text-[9rem] font-light leading-[0.85] tracking-tight text-gradient" style={{ fontFamily: SERIF }}>
              memories,
            </span>
            <span className="block text-7xl md:text-[9rem] font-light leading-[0.85] tracking-tight text-white/40" style={{ fontFamily: SERIF }}>
              reshuffled.
            </span>
          </h1>

          {/* Subtext */}
          <p className="text-white/15 text-base md:text-lg max-w-xl mx-auto leading-relaxed mb-12"
            style={{ fontFamily: SANS, fontWeight: 200, opacity: heroReady ? 1 : 0, transform: heroReady ? 'translateY(0)' : 'translateY(25px)', transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.5s' }}>
            A private media viewer that shuffles through your photos and videos.
            AI search. Local storage. No tracking. Ever.
          </p>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center"
            style={{ opacity: heroReady ? 1 : 0, transform: heroReady ? 'translateY(0)' : 'translateY(20px)', transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.65s' }}>
            <button onClick={handleGetStarted}
              className="group px-10 py-4 rounded-full text-sm font-medium transition-all duration-300 hover:scale-[1.03] flex items-center justify-center gap-3"
              style={{ background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)', color: '#060510', fontFamily: SANS, boxShadow: '0 0 60px rgba(196,181,253,0.15)' }}>
              Start Exploring
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollTo('process')}
              className="px-10 py-4 rounded-full text-sm transition-all duration-300 hover:scale-[1.02]"
              style={{ border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)', fontFamily: SANS, fontWeight: 300 }}>
              See How It Works
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="relative z-10 w-full max-w-3xl mx-auto mt-24 grid grid-cols-3 gap-8">
          <Counter value="∞" label="Local Files" delay={0} />
          <Counter value="200k" label="Drive Items" delay={100} />
          <Counter value="0" label="Data Collected" delay={200} />
        </div>
      </section>

      {/* ─── Marquee ─── */}
      <section className="py-6 border-y border-white/[0.03] overflow-hidden relative z-10">
        <div className="flex" style={{ animation: 'marquee 30s linear infinite' }}>
          {[...Array(2)].map((_, rep) => (
            <div key={rep} className="flex items-center gap-12 text-white/[0.06] text-sm tracking-[.3em] uppercase whitespace-nowrap px-6" style={{ fontFamily: SANS, fontWeight: 200 }}>
              <span>Local-First</span><span className="text-[#C4B5FD]/20">◆</span>
              <span>AI Search</span><span className="text-[#C4B5FD]/20">◆</span>
              <span>Private</span><span className="text-[#C4B5FD]/20">◆</span>
              <span>Shuffle</span><span className="text-[#C4B5FD]/20">◆</span>
              <span>Google Drive</span><span className="text-[#C4B5FD]/20">◆</span>
              <span>No Tracking</span><span className="text-[#C4B5FD]/20">◆</span>
              <span>Collections</span><span className="text-[#C4B5FD]/20">◆</span>
              <span>Full Control</span><span className="text-[#C4B5FD]/20">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Features (horizontal scroll) ─── */}
      <section id="features" className="py-28 px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <SectionLabel text="Features" />
            <h2 className="text-4xl md:text-6xl font-light text-white/80 mt-4" style={{ fontFamily: SERIF }}>
              Everything you need.
              <br />
              <span className="text-white/20">Nothing you don't.</span>
            </h2>
          </div>

          {/* Horizontal scroll */}
          <div ref={scrollRef} className="flex gap-5 overflow-x-auto scroll-container pb-4 -mx-8 px-8">
            <FeatureShowcase icon={<Lock size={18} className="text-[#C4B5FD]" />} title="Your Media, Your Storage" desc="Photos and videos stay in Google Drive or your browser. We only store a small index to remember your selections." num="01" accent="#C4B5FD" />
            <FeatureShowcase icon={<Search size={18} className="text-[#FDA4AF]" />} title="AI-Powered Search" desc="Ask &quot;sunset photos&quot; and find exactly what you're looking for. Natural language, instant results." num="02" accent="#FDA4AF" />
            <FeatureShowcase icon={<Shuffle size={18} className="text-[#A5F3FC]" />} title="Smart Shuffle" desc="Rediscover forgotten memories. Our shuffle surfaces the photos and videos you forgot you had." num="03" accent="#A5F3FC" />
            <FeatureShowcase icon={<Eye size={18} className="text-[#FDE68A]" />} title="Immersive Viewer" desc="Full-screen, distraction-free. Customizable intervals, zoom, pan, and keyboard shortcuts." num="04" accent="#FDE68A" />
            <FeatureShowcase icon={<Shield size={18} className="text-[#6EE7B7]" />} title="Complete Privacy" desc="No tracking, no analytics on your content. Your viewing habits are yours alone." num="05" accent="#6EE7B7" />
            <FeatureShowcase icon={<Zap size={18} className="text-[#DDD6FE]" />} title="Incremental Sync" desc="Only changed files update. Fast, efficient, respectful of your bandwidth." num="06" accent="#DDD6FE" />
          </div>
          <div className="mt-6 flex items-center gap-2 text-white/10 text-xs" style={{ fontFamily: SANS, fontWeight: 200 }}>
            <ChevronRight size={14} /> Scroll to explore
          </div>
        </div>
      </section>

      {/* ─── Process ─── */}
      <section id="process" className="py-28 px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          <SectionLabel text="Process" />
          <h2 className="text-4xl md:text-6xl font-light text-white/80 mt-4 mb-20" style={{ fontFamily: SERIF }}>
            Three steps.<br /><span className="text-white/20">That's it.</span>
          </h2>

          <div className="space-y-0">
            {[
              { num: '01', title: 'Add Your Media', desc: 'Connect your Google Drive or drop files from your device. Everything stays local—nothing is uploaded to our servers.', accent: '#C4B5FD' },
              { num: '02', title: 'AI Tags Your Collection', desc: 'Our AI generates search tags and metadata through secure APIs. Your files never leave your device.', accent: '#FDA4AF' },
              { num: '03', title: 'Explore & Rediscover', desc: 'Shuffle, search with natural language, filter by type. Surface the memories you forgot you had.', accent: '#A5F3FC' },
            ].map((step, i) => {
              const { ref, inView } = useInView();
              return (
                <div key={i} ref={ref}>
                  <div className="flex gap-8 items-start py-12 border-t border-white/[0.04]"
                    style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateX(0)' : 'translateX(40px)', transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 100}ms` }}>
                    <span className="text-5xl md:text-7xl font-light flex-shrink-0" style={{ fontFamily: SERIF, color: step.accent + '30' }}>{step.num}</span>
                    <div className="pt-2 md:pt-4">
                      <h3 className="text-2xl md:text-3xl font-light text-white/70 mb-3" style={{ fontFamily: SERIF }}>{step.title}</h3>
                      <p className="text-white/20 text-sm md:text-base leading-relaxed max-w-lg" style={{ fontFamily: SANS, fontWeight: 200 }}>{step.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Privacy Block ─── */}
      <section className="py-28 px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="liquid-border rounded-[2rem] p-12 md:p-16 mono-glass text-center">
            <Lock size={32} className="text-[#C4B5FD]/30 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-light text-white/80 mb-4" style={{ fontFamily: SERIF }}>
              Your data stays <span className="text-gradient">yours</span>
            </h2>
            <p className="text-white/15 text-base max-w-xl mx-auto mb-10 leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>
              Local-first storage. No tracking. No analytics on your content.
              Connect your own cloud on your terms.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: <Lock size={18} />, text: 'Local Storage' },
                { icon: <Shield size={18} />, text: 'Your Control' },
                { icon: <Eye size={18} />, text: 'No Tracking' },
                { icon: <HardDrive size={18} />, text: 'Secure Sync' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-2.5 text-white/20">
                  <div className="text-[#C4B5FD]/30">{item.icon}</div>
                  <span className="text-xs" style={{ fontFamily: SANS, fontWeight: 200 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-28 px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          <SectionLabel text="Pricing" />
          <h2 className="text-4xl md:text-6xl font-light text-white/80 mt-4 mb-4" style={{ fontFamily: SERIF }}>
            Start free.<br /><span className="text-white/20">Scale when ready.</span>
          </h2>
          <p className="text-white/15 text-sm mb-16" style={{ fontFamily: SANS, fontWeight: 200 }}>No credit card required to get started.</p>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                tier: 'Free', price: '$0', period: 'forever', accent: '#C4B5FD', highlighted: false,
                features: ['2,000 Drive items', 'Unlimited local files', 'Random shuffle', 'Basic keyword search'],
                excluded: ['AI search tags', 'Collections'], cta: 'Get Started',
              },
              {
                tier: 'Pro', price: '$5.99', period: 'month', yearly: '$57.99/yr', accent: '#C4B5FD', highlighted: true, earlyAdopter: 'First 200: $4.99/mo',
                features: ['50,000 Drive items', 'Unlimited local files', 'AI search tags', 'Collections (up to 50)', 'Cloud backup'],
                excluded: [], cta: 'Start Pro Trial',
              },
              {
                tier: 'Power', price: '$19.99', period: 'month', yearly: '$199.99/yr', accent: '#A78BFA', highlighted: false,
                features: ['200,000 Drive items', 'Unlimited local files', 'AI search tags', 'Collections (up to 500)', 'Premium features', 'All Pro features'],
                excluded: [], cta: 'Start Power Trial',
              },
            ].map((plan, i) => {
              const { ref, inView } = useInView();
              return (
                <div key={i} ref={ref}
                  className={`rounded-3xl p-8 transition-all duration-700 ${plan.highlighted ? 'liquid-border' : 'mono-glass mono-glass-hover'}`}
                  style={{
                    opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${i * 100}ms`,
                    ...(plan.highlighted ? { background: 'rgba(196,181,253,0.04)', border: '1px solid rgba(196,181,253,0.15)', boxShadow: '0 0 80px rgba(196,181,253,0.05)' } : {}),
                  }}>
                  {plan.highlighted && (
                    <span className="inline-block px-3 py-1 rounded-full text-[10px] tracking-[.2em] uppercase mb-4"
                      style={{ background: 'rgba(196,181,253,0.15)', color: '#C4B5FD', fontFamily: SANS }}>Popular</span>
                  )}
                  <h3 className="text-xl font-light text-white/60 mb-1" style={{ fontFamily: SERIF }}>{plan.tier}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-light" style={{ fontFamily: SERIF, color: plan.accent }}>{plan.price}</span>
                    <span className="text-white/15 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>/{plan.period}</span>
                  </div>
                  <div className="mb-6">
                    {plan.yearly && <p className="text-white/10 text-xs" style={{ fontFamily: SANS, fontWeight: 200 }}>{plan.yearly}</p>}
                    {(plan as { earlyAdopter?: string }).earlyAdopter && <p className="text-white/20 text-[10px] mt-0.5" style={{ fontFamily: SANS, fontWeight: 200 }}>{(plan as { earlyAdopter?: string }).earlyAdopter}</p>}
                    {!plan.yearly && !(plan as { earlyAdopter?: string }).earlyAdopter && <div />}
                  </div>
                  <ul className="space-y-2.5 mb-8">
                    {plan.features.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-2.5 text-white/30 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
                        <Check size={12} style={{ color: plan.accent }} />{f}
                      </li>
                    ))}
                    {plan.excluded?.map((f, fi) => (
                      <li key={`ex-${fi}`} className="flex items-center gap-2.5 text-white/10 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
                        <X size={12} />{f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={handleGetStarted}
                    className="w-full py-3.5 rounded-2xl text-sm transition-all duration-300 hover:scale-[1.02]"
                    style={plan.highlighted
                      ? { background: `linear-gradient(135deg, ${plan.accent}, ${plan.accent}80)`, color: '#060510', fontFamily: SANS, fontWeight: 500 }
                      : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)', fontFamily: SANS, fontWeight: 300 }}>
                    {plan.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-32 px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-7xl md:text-[8rem] font-light leading-[0.85] mb-8" style={{ fontFamily: SERIF }}>
            <span className="text-white/80">Go.</span>
          </div>
          <p className="text-white/15 text-base mb-10 max-w-md mx-auto" style={{ fontFamily: SANS, fontWeight: 200 }}>
            Your media library is waiting to be rediscovered. Start in seconds.
          </p>
          <button onClick={handleGetStarted}
            className="group px-12 py-4 rounded-full text-sm font-medium transition-all duration-300 hover:scale-[1.03] inline-flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)', color: '#060510', fontFamily: SANS, boxShadow: '0 0 60px rgba(196,181,253,0.15)' }}>
            Get Started Free
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-white/[0.03] py-16 px-8 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/refract-logo.png" alt="Refract" className="w-8 h-8 rounded-md opacity-30" />
              <span className="text-white/20 text-sm tracking-[.15em] uppercase" style={{ fontFamily: SANS, fontWeight: 300 }}>Refract</span>
            </div>
            <p className="text-white/10 text-xs max-w-xs" style={{ fontFamily: SANS, fontWeight: 200 }}>
              Your private media library. Made for creators, by creators.
            </p>
          </div>
          <div className="flex gap-16">
            {[
              { title: 'Product', links: [{ text: 'Features', action: () => scrollTo('features') }, { text: 'Pricing', action: () => scrollTo('pricing') }] },
              { title: 'Legal', links: [{ text: 'Privacy', href: '/privacy' }, { text: 'Terms', href: '/terms' }] },
              { title: 'Connect', links: [{ text: 'Twitter', href: '#' }, { text: 'GitHub', href: '#' }] },
            ].map((col, i) => (
              <div key={i}>
                <h4 className="text-white/20 text-[10px] tracking-[.25em] uppercase mb-4" style={{ fontFamily: SANS, fontWeight: 300 }}>{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.links.map((l, li) => (
                    <li key={li}>
                      {'action' in l
                        ? <button onClick={l.action} className="text-white/10 hover:text-white/30 text-sm transition-colors" style={{ fontFamily: SANS, fontWeight: 200 }}>{l.text}</button>
                        : <a href={(l as any).href} className="text-white/10 hover:text-white/30 text-sm transition-colors" style={{ fontFamily: SANS, fontWeight: 200 }}>{l.text}</a>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-white/[0.03] text-white/10 text-xs text-center" style={{ fontFamily: SANS, fontWeight: 200 }}>
          © 2026 Refract. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

// ─── Section label ───
const SectionLabel: React.FC<{ text: string }> = ({ text }) => (
  <span className="text-[10px] tracking-[.4em] uppercase" style={{ fontFamily: SANS, fontWeight: 300, color: 'rgba(196,181,253,0.3)' }}>{text}</span>
);

export default HomepageDesign2;
